import axios from "axios";

export const LANGS = ["en", "de", "fr", "es"];
const path = window.location.pathname;
const prefix = LANGS.slice(1).find((l) => path === `/${l}` || path.startsWith(`/${l}/`));
export const LANG = prefix || "en";
export const BASENAME = LANG === "en" ? undefined : `/${LANG}`;

const API = `${process.env.REACT_APP_BACKEND_URL}/api/i18n/${LANG}`;
const CACHE_KEY = `pd-i18n-${LANG}`;
const SKIP = new Set(["SCRIPT", "STYLE", "NOSCRIPT", "CODE", "PRE", "TEXTAREA", "SVG"]);
const ATTRS = ["placeholder", "aria-label", "alt", "title"];
const done = new WeakMap();

let dict = {};
const missing = new Map();
let timer = null;

const stripLang = () => (LANG === "en" ? window.location.pathname : window.location.pathname.slice(LANG.length + 1) || "/");
const withLang = (l, rest) => (l === "en" ? rest : `/${l}${rest === "/" ? "" : rest}`);

export const switchLang = (to) => {
  if (to === LANG) return;
  window.location.href = withLang(to, stripLang()) + window.location.search + window.location.hash;
};

export const setAlternates = () => {
  const rest = stripLang();
  const origin = window.location.origin;
  const urls = Object.fromEntries(LANGS.map((l) => [l, origin + withLang(l, rest)]));
  urls["x-default"] = origin + rest;
  Object.entries(urls).forEach(([hl, href]) => {
    let l = document.head.querySelector(`link[rel="alternate"][hreflang="${hl}"]`);
    if (!l) {
      l = document.createElement("link");
      l.rel = "alternate";
      l.hreflang = hl;
      document.head.appendChild(l);
    }
    l.href = href;
  });
};

const flush = () => {
  timer = null;
  const items = [...missing].map(([t, c]) => ({ t, c }));
  missing.clear();
  for (let i = 0; i < items.length; i += 300) axios.post(`${API}/missing`, { items: items.slice(i, i + 300) }).catch(() => {});
};

const queue = (t, ctx) => {
  if (t.length > 1500 || missing.has(t)) return;
  missing.set(t, ctx && ctx !== t ? ctx.slice(0, 600) : "");
  if (!timer) timer = setTimeout(flush, 2000);
};

const BLOCK = "h1,h2,h3,h4,h5,h6,p,li,a,button,label,figcaption,blockquote,dt,dd,td,th,title";
const contextOf = (el) => (el?.closest(BLOCK)?.textContent || "").replace(/\s+/g, " ").trim();

const lookup = (raw, el) => {
  const m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
  const core = m[2];
  if (!core || !/[A-Za-z]/.test(core)) return null;
  const t = dict[core];
  if (t == null) { queue(core, el ? contextOf(el) : ""); return null; }
  return m[1] + t + m[3];
};

const blocked = (el) => window.location.pathname.startsWith(`${BASENAME}/admin`) || !!el.closest("[data-no-translate]")
  || (el.closest("head") && document.documentElement.dataset.seoRaw === "1");

const doText = (node) => {
  const v = node.nodeValue;
  if (done.get(node) === v) return;
  const p = node.parentElement;
  if (!p || SKIP.has(p.tagName.toUpperCase()) || blocked(p)) return;
  const out = lookup(v, p);
  if (out != null && out !== v) { done.set(node, out); node.nodeValue = out; }
};

const attrsFor = (el) => (el.tagName === "META" ? (el.name === "description" ? ["content"] : []) : ATTRS);

const doAttrs = (el) => {
  const list = attrsFor(el);
  if (!list.length || blocked(el)) return;
  let rec = done.get(el);
  list.forEach((a) => {
    const v = el.getAttribute(a);
    if (!v || rec?.[a] === v) return;
    const out = lookup(v);
    if (out != null && out !== v) {
      rec = rec || {};
      rec[a] = out;
      done.set(el, rec);
      el.setAttribute(a, out);
    }
  });
};

const walk = (root) => {
  if (root.nodeType === 3) { doText(root); return; }
  if (root.nodeType !== 1) return;
  const w = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let n = root;
  while (n) {
    if (n.nodeType === 3) doText(n);
    else doAttrs(n);
    n = w.nextNode();
  }
};

const onMutations = (list) => {
  list.forEach((m) => {
    if (m.type === "characterData") doText(m.target);
    else if (m.type === "attributes") doAttrs(m.target);
    else m.addedNodes.forEach(walk);
  });
};

const load = () => axios.get(API).then((r) => {
  const strings = r.data?.strings;
  if (!strings || typeof strings !== "object") return;
  dict = strings;
  try { localStorage.setItem(CACHE_KEY, JSON.stringify(strings)); } catch { /* storage full */ }
  walk(document.documentElement);
});

export const startTranslator = () => {
  if (LANG === "en") return;
  document.documentElement.lang = LANG;
  let cached = null;
  try { cached = JSON.parse(localStorage.getItem(CACHE_KEY) || "null"); } catch { cached = null; }
  const root = document.documentElement;
  if (cached) dict = cached;
  else root.style.visibility = "hidden";
  const reveal = () => { root.style.visibility = ""; };
  const safety = setTimeout(reveal, 2500);
  new MutationObserver(onMutations).observe(root, { childList: true, subtree: true, characterData: true, attributes: true, attributeFilter: [...ATTRS, "content"] });
  walk(root);
  load().catch(() => {}).finally(() => { clearTimeout(safety); reveal(); });
};
